import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Card } from './Card';

export interface StatProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  /** Ícone/chip no topo do card. Passe um glyph ou um <IconBox> já tonalizado. */
  icon?: ReactNode;
  /** Cor do VALOR (não do ícone). */
  tone?: 'default' | 'accent' | 'ok' | 'warn' | 'danger';
}

const TONE_CLASS: Record<NonNullable<StatProps['tone']>, string> = {
  default: 'text-text',
  accent: 'text-accent',
  ok: 'text-[var(--success-fg)]',
  warn: 'text-[var(--warning-fg)]',
  danger: 'text-[var(--danger-fg)]',
};

// Card de métrica ARA: ícone/chip em cima, micro-label, valor grande, hint.
// O ícone é passado pelo consumidor (glyph solto ou <IconBox> tonalizado) —
// a primitiva não impõe cor de chip, só o layout e a tipografia.
export function Stat({
  label,
  value,
  hint,
  icon,
  tone = 'default',
  className,
  ...rest
}: StatProps) {
  return (
    <Card className={cn('flex flex-col gap-3.5', className)} {...rest}>
      {icon && <div className="shrink-0">{icon}</div>}
      <div className="flex min-w-0 flex-col gap-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
          {label}
        </span>
        <span
          className={cn(
            'text-[20px] font-semibold leading-[24px] tracking-tight',
            TONE_CLASS[tone],
          )}
        >
          {value}
        </span>
        {hint && <span className="text-xs leading-[17px] text-text-2">{hint}</span>}
      </div>
    </Card>
  );
}
